import React, { useEffect, useState } from 'react';
import {
  Shield,
  UserCog,
  UserPlus,
  Power,
  Loader2
} from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/lib/auth';
import { errorMessage } from '@/lib/utils';

// PHASE 1 (multi-venue): this page now serves two audiences.
//   - super_admin: creates venue admins (and, as before, operators too),
//     assigned to a chosen venue — see the "Venue" select below.
//   - venue admin ("admin" role): self-serves their own operators, always
//     scoped to their own venue automatically — no venue or role picker
//     needed, and it hits POST /admin/operators instead of POST /admin/users.
export default function AdminUsers() {

  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);

  const [venues, setVenues] = useState([]);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'operator',
    academy_id: '',
  });

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data);
    } catch (err) {
      toast({
        title: 'Failed to load users',
        description: errorMessage(err.response?.data?.detail, 'Error'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    if (isSuperAdmin) {
      api.get('/admin/academies').then((r) => setVenues(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const set = (k) => (e) => {
    setForm({
      ...form,
      [k]: e.target.value,
    });
  };

  const createUser = async (e) => {

    e.preventDefault();

    if (isSuperAdmin && venues.length > 1 && !form.academy_id) {
      toast({ title: 'Choose a venue', description: 'More than one venue exists — pick which one this account manages', variant: 'destructive' });
      return;
    }

    setCreating(true);

    try {

      if (isSuperAdmin) {
        await api.post('/admin/users', form);
      } else {
        // academy_admin self-service — no role/academy fields to send
        await api.post('/admin/operators', { name: form.name, email: form.email, phone: form.phone, password: form.password });
      }

      toast({
        title: 'User created',
        description: isSuperAdmin ? `${form.role} account added` : 'Operator account added',
        variant: 'success',
      });

      setForm({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'operator',
        academy_id: '',
      });

      loadUsers();

    } catch (err) {

      toast({
        title: 'Failed',
        description: errorMessage(err.response?.data?.detail, 'Unable to create user'),
        variant: 'destructive',
      });

    } finally {
      setCreating(false);
    }
  };

  const toggleUser = async (id) => {

    try {

      await api.put(`/admin/users/${id}/toggle`);

      toast({
        title: 'User updated',
        variant: 'success',
      });

      loadUsers();

    } catch (err) {

      toast({
        title: 'Failed',
        description: errorMessage(err.response?.data?.detail, 'Unable to update'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div
      className="space-y-8"
      data-testid="admin-users-page"
    >

      {/* HEADER */}

      <div className="flex items-end justify-between gap-4">

        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">
            Access Control
          </div>

          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
            {isSuperAdmin ? 'Admin Users' : 'My Operators'}
          </h1>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-xs text-white/50">
          <Shield size={14} />
          {isSuperAdmin ? 'Super Admin Only' : 'Venue Admin'}
        </div>
      </div>

      {/* CREATE USER */}

      <div className="bg-surface border border-white/10 p-6">

        <div className="flex items-center gap-2 mb-6">
          <UserPlus size={18} className="text-volt" />
          <h2 className="font-heading font-bold uppercase text-lg">
            {isSuperAdmin ? 'Create Admin User' : 'Create Operator'}
          </h2>
        </div>

        <form
          onSubmit={createUser}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >

          <Field label="Full Name">
            <Input
              required
              value={form.name}
              onChange={set('name')}
              placeholder="PaceSlot Operator"
            />
          </Field>

          <Field label="Email">
            <Input
              required
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="operator@kba.com"
            />
          </Field>

          <Field label="Phone">
            <Input
              required
              value={form.phone}
              onChange={set('phone')}
              placeholder="+91 9876543210"
            />
          </Field>

          <Field label="Password" hint="8+ characters, with an uppercase letter, lowercase letter, number, and special character.">
            <Input
              required
              type="password"
              minLength={8}
              value={form.password}
              onChange={set('password')}
              placeholder="At least 8 characters"
            />
          </Field>

          {isSuperAdmin && (
            <Field label="Role">
              <select
                value={form.role}
                onChange={set('role')}
                className="
                  h-12
                  w-full
                  border
                  border-white/10
                  bg-obsidian
                  px-4
                  text-sm
                  text-white
                  outline-none
                "
              >
                <option value="admin">
                  Admin (Venue Admin)
                </option>

                <option value="operator">
                  Operator
                </option>
              </select>
            </Field>
          )}

          {isSuperAdmin && (
            <Field label="Venue">
              <select
                value={form.academy_id}
                onChange={set('academy_id')}
                className="
                  h-12
                  w-full
                  border
                  border-white/10
                  bg-obsidian
                  px-4
                  text-sm
                  text-white
                  outline-none
                "
              >
                <option value="">
                  {venues.length <= 1 ? 'Default venue (auto)' : 'Choose a venue…'}
                </option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </Field>
          )}

          <div className="flex items-end">

            <Button
              type="submit"
              disabled={creating}
              className="w-full"
            >
              {creating ? (
                <>
                  <Loader2
                    size={14}
                    className="animate-spin"
                  />
                  Creating...
                </>
              ) : (
                <>
                  Create User
                </>
              )}
            </Button>

          </div>
        </form>
      </div>

      {/* USERS TABLE */}

      <div className="bg-surface border border-white/10 overflow-x-auto">

        <table className="w-full min-w-[900px] text-sm">

          <thead className="border-b border-white/10 bg-obsidian/40">

            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">

              <th className="text-left px-5 py-4">
                User
              </th>

              <th className="text-left px-5 py-4">
                Contact
              </th>

              <th className="text-left px-5 py-4">
                Role
              </th>

              {isSuperAdmin && (
                <th className="text-left px-5 py-4">
                  Venue
                </th>
              )}

              <th className="text-left px-5 py-4">
                Status
              </th>

              <th className="text-right px-5 py-4">
                Actions
              </th>

            </tr>
          </thead>

          <tbody>

            {loading ? (

              <tr>
                <td
                  colSpan={isSuperAdmin ? 6 : 5}
                  className="px-5 py-12 text-center text-white/40"
                >
                  Loading users...
                </td>
              </tr>

            ) : users.length === 0 ? (

              <tr>
                <td
                  colSpan={isSuperAdmin ? 6 : 5}
                  className="px-5 py-12 text-center text-white/40"
                >
                  No users found
                </td>
              </tr>

            ) : users.map((u) => (

              <tr
                key={u.id}
                className="border-b border-white/5 hover:bg-white/5"
              >

                <td className="px-5 py-4">

                  <div className="flex items-center gap-3">

                    <div className="
                      w-10
                      h-10
                      rounded-full
                      bg-volt/10
                      flex
                      items-center
                      justify-center
                    ">
                      <UserCog
                        size={18}
                        className="text-volt"
                      />
                    </div>

                    <div>
                      <div className="font-medium">
                        {u.name}
                      </div>

                      <div className="text-[10px] uppercase tracking-wider text-white/40">
                        {u.id.slice(0, 8)}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4 text-xs text-white/70">

                  <div>
                    {u.email}
                  </div>

                  <div className="text-white/40">
                    {u.phone}
                  </div>

                </td>

                <td className="px-5 py-4">

                  <span className={`
                    px-3
                    py-1
                    text-[10px]
                    uppercase
                    tracking-wider
                    border

                    ${u.role === 'super_admin'
                      ? 'border-yellow-500 text-yellow-400'
                      : u.role === 'admin'
                      ? 'border-volt text-volt'
                      : 'border-cyan-500 text-cyan-400'
                    }
                  `}>
                    {u.role.replace('_', ' ')}
                  </span>

                </td>

                {isSuperAdmin && (
                  <td className="px-5 py-4 text-xs text-white/50">
                    {u.academy_name || '—'}
                  </td>
                )}

                <td className="px-5 py-4">

                  <span className={`
                    text-xs
                    ${u.is_active
                      ? 'text-green-400'
                      : 'text-red-400'
                    }
                  `}>
                    {u.is_active ? 'Active' : 'Disabled'}
                  </span>

                </td>

                <td className="px-5 py-4 text-right">

                  {u.role !== 'super_admin' && (

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleUser(u.id)}
                    >
                      <Power size={14} />

                      {u.is_active
                        ? 'Disable'
                        : 'Enable'
                      }
                    </Button>

                  )}

                </td>

              </tr>

            ))}

          </tbody>
        </table>
      </div>
    </div>
  );
}